/**
 * Bet History Service
 * เรียก Cloud Functions สำหรับ Bet History
 */

import { callCloudFunction } from '@/services/adapters/parse/parse-client';
import type {
  BetHistoryItem,
  BetHistorySummary,
  BetHistoryResponse,
  BetHistorySummaryResponse,
  BetHistoryFilters,
  BetHistoryStats,
} from './bet-history.types';

export const BetHistoryService = {
  /**
   * ดึงประวัติการเดิมพันของสมาชิก
   */
  async getMemberBetHistory(
    memberId: string,
    options: {
      limit?: number;
      skip?: number;
      filters?: BetHistoryFilters;
    } = {}
  ): Promise<{ data: BetHistoryItem[]; pagination: BetHistoryResponse['pagination'] }> {
    const { limit = 50, skip = 0, filters = {} } = options;

    try {
      const response = await callCloudFunction('getMemberBetHistory', {
        memberId,
        limit,
        skip,
        ...filters,
      }) as BetHistoryResponse;

      if (response.success) {
        return {
          data: response.data.map((item: BetHistoryItem) => ({
            ...item,
            bet_time: new Date(item.bet_time),
            sync_date: new Date(item.sync_date),
          })),
          pagination: response.pagination,
        };
      }

      throw new Error('Failed to fetch bet history');
    } catch (error) {
      console.error('[BetHistoryService] getMemberBetHistory error:', error);
      return {
        data: [],
        pagination: { total: 0, limit, skip, hasMore: false },
      };
    }
  },

  /**
   * ดึงสรุปยอดการเดิมพันของสมาชิก
   */
  async getMemberBetSummary(memberId: string): Promise<BetHistorySummary | null> {
    try {
      const response = await callCloudFunction('getMemberBetSummary', {
        memberId,
      }) as BetHistorySummaryResponse;

      if (response.success) {
        return response.data;
      }

      throw new Error('Failed to fetch bet summary');
    } catch (error) {
      console.error('[BetHistoryService] getMemberBetSummary error:', error);
      return null;
    }
  },

  /**
   * Refresh ประวัติการเดิมพันของสมาชิก (Admin only)
   */
  async refreshMemberBetHistory(
    memberId: string,
    days: number = 7
  ): Promise<{ synced: number; errors: unknown[] }> {
    try {
      const response = await callCloudFunction('refreshMemberBetHistory', {
        memberId,
        days,
      }) as { success: boolean; data: { synced: number; errors: unknown[] } };

      if (response.success) {
        return response.data;
      }

      throw new Error('Failed to refresh bet history');
    } catch (error) {
      console.error('[BetHistoryService] refreshMemberBetHistory error:', error);
      throw error;
    }
  },

  /**
   * ดึงรายชื่อ Providers ที่มีในประวัติ
   */
  async getBetHistoryProviders(memberId?: string): Promise<string[]> {
    try {
      const response = await callCloudFunction('getBetHistoryProviders', {
        memberId,
      }) as { success: boolean; data: string[] };

      if (response.success) {
        return response.data;
      }

      return [];
    } catch (error) {
      console.error('[BetHistoryService] getBetHistoryProviders error:', error);
      return [];
    }
  },

  /**
   * ดึง Statistics (Admin only)
   */
  async getBetHistoryStats(days: number = 7): Promise<BetHistoryStats | null> {
    try {
      const response = await callCloudFunction('getBetHistoryStats', {
        days,
      }) as { success: boolean; data: BetHistoryStats };

      if (response.success) {
        return response.data;
      }

      return null;
    } catch (error) {
      console.error('[BetHistoryService] getBetHistoryStats error:', error);
      return null;
    }
  },
};

export default BetHistoryService;
